<script setup>
</script>
<template>
  <Layout>
    <div ref="dashboardShell" class="dashboard-shell mt-4">
      <Transition name="floating-toolbar">
        <div
          v-if="mostrarBarraFlutuante"
          ref="dashboardFloatingBar"
          class="dashboard-floating-bar"
          :style="floatingBarStyle"
        >
          <div class="dashboard-floating-bar-inner">
            <div class="dashboard-floating-primary">
              <BButton variant="primary" class="hero-action-primary dashboard-floating-action" @click="abrirNovoChamado">
                <i class="ri-add-line align-bottom me-1"></i> Abrir chamado
              </BButton>
              <BButton variant="light" class="hero-action-secondary dashboard-floating-action" @click="irParaChamados">
                <i class="ri-file-list-3-line align-bottom me-1"></i> Ver meus chamados
              </BButton>
              <!-- Na barra flutuante o atalho vira só ícone: ela acompanha o
                   scroll e precisa caber no mobile. O contador segue visível. -->
              <BButton
                variant="light"
                class="hero-action-secondary dashboard-floating-action dashboard-floating-termos"
                :title="termosPendentes > 0 ? `${termosPendentes} termo(s) pendente(s)` : 'Meus termos'"
                @click="irParaTermos"
              >
                <i class="ri-file-shield-2-line align-bottom"></i>
                <span class="d-none d-md-inline ms-1">Termos</span>
                <span v-if="termosPendentes > 0" class="hero-action-contador">{{ termosPendentes }}</span>
              </BButton>
            </div>

            <div class="dashboard-floating-secondary">
              <div class="dashboard-floating-shortcuts" aria-label="Atalhos rápidos">
                <div
                  v-for="categoria in atalhosRapidosOrdenados"
                  :key="`atalho-barra-grupo-${categoria.titulo}`"
                  class="dashboard-floating-shortcut-group"
                  :aria-label="categoria.titulo"
                >
                  <template
                    v-for="atalho in categoria.itens"
                    :key="`atalho-barra-${categoria.titulo}-${atalho.titulo}`"
                  >
                    <a
                      :id="getAtalhoPopoverId('barra', categoria.titulo, atalho.titulo)"
                      :href="atalho.link"
                      class="dashboard-floating-shortcut"
                      :aria-label="atalho.titulo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span
                        class="hero-shortcut-icon dashboard-floating-shortcut-icon"
                        :class="atalho.imagem ? 'hero-shortcut-icon-app' : atalho.cor"
                      >
                        <img
                          v-if="atalho.imagem"
                          :src="atalho.imagem"
                          :alt="atalho.titulo"
                          class="hero-shortcut-app-image"
                        />
                        <i v-else :class="atalho.icone"></i>
                      </span>
                    </a>
                    <BPopover
                      :target="getAtalhoPopoverId('barra', categoria.titulo, atalho.titulo)"
                      container="body"
                      triggers="hover focus"
                      placement="top"
                      strategy="fixed"
                      boundary="viewport"
                      :boundary-padding="8"
                      :offset="10"
                    >
                      {{ atalho.titulo }}
                    </BPopover>
                  </template>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-light hero-action-secondary dashboard-floating-menu-button"
                :aria-expanded="menuAtalhosAberto ? 'true' : 'false'"
                aria-controls="dashboard-floating-shortcuts-panel"
                @click="toggleAtalhosBarraFlutuante"
              >
                <i :class="menuAtalhosAberto ? 'ri-close-line' : 'ri-menu-3-line'"></i>
              </button>
            </div>
          </div>

          <Transition name="floating-shortcuts-panel">
            <div
              v-if="menuAtalhosAberto"
              id="dashboard-floating-shortcuts-panel"
              class="dashboard-floating-shortcuts-panel"
            >
              <div class="dashboard-floating-panel-groups">
                <article
                  v-for="categoria in atalhosRapidosOrdenados"
                  :key="`atalho-menu-${categoria.titulo}`"
                  class="dashboard-floating-panel-group"
                >
                  <span class="dashboard-floating-panel-group-title">{{ categoria.titulo }}</span>
                  <div class="dashboard-floating-panel-grid">
                    <template
                      v-for="atalho in categoria.itens"
                      :key="`atalho-menu-item-${atalho.titulo}`"
                    >
                      <a
                        :id="getAtalhoPopoverId('painel', categoria.titulo, atalho.titulo)"
                        :href="atalho.link"
                        class="dashboard-floating-shortcut dashboard-floating-shortcut-panel"
                        :aria-label="atalho.titulo"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click="fecharAtalhosBarraFlutuante"
                      >
                        <span
                          class="hero-shortcut-icon dashboard-floating-shortcut-icon"
                          :class="atalho.imagem ? 'hero-shortcut-icon-app' : atalho.cor"
                        >
                          <img
                            v-if="atalho.imagem"
                            :src="atalho.imagem"
                            :alt="atalho.titulo"
                            class="hero-shortcut-app-image"
                          />
                          <i v-else :class="atalho.icone"></i>
                        </span>
                      </a>
                      <BPopover
                        :target="getAtalhoPopoverId('painel', categoria.titulo, atalho.titulo)"
                        container="body"
                        triggers="hover focus"
                        placement="top"
                        strategy="fixed"
                        boundary="viewport"
                        :boundary-padding="8"
                        :offset="10"
                      >
                        {{ atalho.titulo }}
                      </BPopover>
                    </template>
                  </div>
                </article>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>

      <section v-if="termosPendentes > 0" class="dashboard-termo-aviso" role="alert">
        <div class="dashboard-termo-aviso-icone"><i class="ri-file-shield-2-line"></i></div>
        <div class="dashboard-termo-aviso-texto">
          <strong>
            Você tem {{ termosPendentes }} termo(s) de responsabilidade aguardando assinatura.
          </strong>
          <span>
            Leia o documento do equipamento sob sua guarda e assine com a senha da sua conta de rede.
          </span>
        </div>
        <BButton variant="warning" class="dashboard-termo-aviso-acao" @click="irParaTermos">
          <i class="ri-quill-pen-line align-bottom me-1"></i> Ler e assinar
        </BButton>
      </section>

      <section v-if="chamadosComResposta > 0" class="dashboard-resposta-aviso" role="status">
        <div class="dashboard-resposta-aviso-icone"><i class="ri-chat-3-fill"></i></div>
        <div class="dashboard-resposta-aviso-texto">
          <strong>
            {{ chamadosComResposta }} chamado(s) com resposta nova da equipe.
          </strong>
          <span>Abra o chamado para ler o retorno e responder, se precisar.</span>
        </div>
        <BButton variant="primary" class="dashboard-resposta-aviso-acao" @click="irParaChamados">
          <i class="ri-file-list-3-line align-bottom me-1"></i> Ver meus chamados
        </BButton>
      </section>

      <section ref="dashboardHeroGrid" class="dashboard-hero">
        <div class="dashboard-hero-top">
          <div class="dashboard-hero-main">
            <span class="hero-badge">Central de atendimento de TI</span>
            <h2 class="hero-title">
              Bem-vindo(a), <span class="hero-title-highlight">{{ user.nome }}</span>
            </h2>
            <p class="hero-description">
              Use esta tela para abrir chamados de suporte, acompanhar o andamento das suas
              solicitações e acessar rapidamente os principais sistemas institucionais.
            </p>

            <div ref="dashboardHeroActions" class="hero-actions">
              <BButton variant="primary" class="hero-action-primary" @click="abrirNovoChamado">
                <i class="ri-add-line align-bottom me-1"></i> Abrir chamado
              </BButton>
              <BButton variant="light" class="hero-action-secondary" @click="irParaChamados">
                <i class="ri-file-list-3-line align-bottom me-1"></i> Ver meus chamados
              </BButton>
              <!-- Caminho permanente para os termos: o aviso âmbar só aparece
                   quando há pendência, e sem esta porta o usuário que já assinou
                   não teria como rever os próprios documentos. -->
              <BButton variant="light" class="hero-action-secondary hero-action-termos" @click="irParaTermos">
                <i class="ri-file-shield-2-line align-bottom me-1"></i> Meus termos
                <span v-if="termosPendentes > 0" class="hero-action-contador">{{ termosPendentes }}</span>
              </BButton>
            </div>

            <div class="hero-features">
              <article
                v-for="acao in acoesPainel"
                :key="acao.titulo"
                class="hero-feature"
              >
                <span class="hero-feature-icon">
                  <i :class="acao.icone"></i>
                </span>
                <div>
                  <h3 class="hero-feature-title">{{ acao.titulo }}</h3>
                  <p class="hero-feature-text">{{ acao.descricao }}</p>
                </div>
              </article>
            </div>
          </div>

          <aside ref="dashboardShortcutPanel" class="dashboard-hero-side">
            <div class="hero-side-header">
              <span class="hero-badge mb-2">Atalhos rápidos</span>
            </div>
            <div class="hero-shortcut-groups">
              <article
                v-for="categoria in atalhosRapidosOrdenados"
                :key="categoria.titulo"
                class="hero-shortcut-group"
              >
                <span class="hero-shortcut-group-title">{{ categoria.titulo }}</span>
                <div class="hero-shortcut-row">
                  <template v-for="atalho in categoria.itens" :key="atalho.titulo">
                    <a
                      :id="getAtalhoPopoverId('hero', categoria.titulo, atalho.titulo)"
                      :href="atalho.link"
                      class="hero-shortcut-item"
                      :aria-label="atalho.titulo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        v-if="atalho.imagem"
                        :src="atalho.imagem"
                        :alt="atalho.titulo"
                        class="hero-shortcut-app-image"
                      />
                      <i v-else :class="atalho.icone"></i>
                    </a>
                    <BPopover
                      :target="getAtalhoPopoverId('hero', categoria.titulo, atalho.titulo)"
                      container="body"
                      triggers="hover focus"
                      placement="top"
                      strategy="fixed"
                      boundary="viewport"
                      :boundary-padding="8"
                      :offset="10"
                    >
                      {{ atalho.titulo }}
                    </BPopover>
                  </template>
                </div>
              </article>
            </div>
          </aside>
        </div>

      </section>

      <section class="dashboard-hero">
        <div class="section-header section-header-inline">
          <div>
            <span class="hero-badge mb-3">Acompanhamento</span>
            <h3 class="section-title">Panorama dos seus chamados</h3>
            <p class="section-description mb-2">
              Consulte rapidamente quantos chamados estão novos, pendentes, em execução ou concluídos.
            </p>
          </div>
        </div>

        <card-glpi ref="card-glpi" />
      </section>

      <div id="area-chamados">
        <chamado
          ref="chamados"
          @createEvent="createEvent"
          @nao-lidos="chamadosComResposta = $event"
        />
      </div>

      <!-- Termos moram na mesma página que os chamados: o CAU não tem menu
           lateral, e seção rolável é o padrão já estabelecido aqui. -->
      <div id="area-termos">
        <termos-section ref="termos" @pendentes="termosPendentes = $event" />
      </div>
    </div>
  </Layout>
</template>
<style scoped>
/* Aviso de termo de responsabilidade pendente. Fica acima do hero de propósito:
   é obrigação do usuário, não um atalho a mais. */
.dashboard-termo-aviso {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid rgba(245, 158, 11, 0.32);
  border-left: 4px solid #f59e0b;
  border-radius: 1rem;
  background: rgba(245, 158, 11, 0.09);
}

.dashboard-termo-aviso-icone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 0.9rem;
  background: rgba(245, 158, 11, 0.18);
  color: #b45309;
  font-size: 1.25rem;
}

.dashboard-termo-aviso-texto {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1 1 320px;
  color: #92400e;
  font-size: 0.88rem;
}

.dashboard-termo-aviso-acao {
  flex: 0 0 auto;
}

[data-bs-theme="dark"] .dashboard-termo-aviso,
[data-layout-mode="dark"] .dashboard-termo-aviso {
  background: rgba(245, 158, 11, 0.14);
  border-color: rgba(245, 158, 11, 0.3);
}

[data-bs-theme="dark"] .dashboard-termo-aviso-texto,
[data-layout-mode="dark"] .dashboard-termo-aviso-texto {
  color: #fcd34d;
}

[data-bs-theme="dark"] .dashboard-termo-aviso-icone,
[data-layout-mode="dark"] .dashboard-termo-aviso-icone {
  background: rgba(245, 158, 11, 0.22);
  color: #fcd34d;
}

/* Contador no atalho de termos. Fica junto ao rótulo (e não sobreposto ao
   canto) para continuar legível no botão claro e no modo escuro. */
.hero-action-termos,
.dashboard-floating-termos {
  position: relative;
}

.hero-action-contador {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  margin-left: 0.4rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #f59e0b;
  color: #1f2937;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
}

/* Resposta nova em chamado. Tom informativo (azul), diferente do termo
   pendente (âmbar): um é retorno da equipe, o outro é obrigação do usuário. */
.dashboard-resposta-aviso {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid rgba(13, 110, 253, 0.28);
  border-left: 4px solid #0d6efd;
  border-radius: 1rem;
  background: rgba(13, 110, 253, 0.07);
}

.dashboard-resposta-aviso-icone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 0.9rem;
  background: rgba(13, 110, 253, 0.16);
  color: #0a58ca;
  font-size: 1.25rem;
}

.dashboard-resposta-aviso-texto {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1 1 320px;
  color: #084298;
  font-size: 0.88rem;
}

.dashboard-resposta-aviso-acao {
  flex: 0 0 auto;
}

[data-bs-theme="dark"] .dashboard-resposta-aviso,
[data-layout-mode="dark"] .dashboard-resposta-aviso {
  background: rgba(13, 110, 253, 0.14);
  border-color: rgba(13, 110, 253, 0.32);
}

[data-bs-theme="dark"] .dashboard-resposta-aviso-texto,
[data-layout-mode="dark"] .dashboard-resposta-aviso-texto {
  color: #9ec5fe;
}

[data-bs-theme="dark"] .dashboard-resposta-aviso-icone,
[data-layout-mode="dark"] .dashboard-resposta-aviso-icone {
  background: rgba(13, 110, 253, 0.24);
  color: #9ec5fe;
}

@media (max-width: 575.98px) {
  .dashboard-termo-aviso-acao,
  .dashboard-resposta-aviso-acao {
    width: 100%;
  }
}

.dashboard-shell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem 0 0.75rem;
  --dashboard-surface:
    radial-gradient(circle at top right, rgba(13, 110, 253, 0.12), transparent 28%),
    linear-gradient(135deg, #ffffff 0%, #f4f9ff 52%, #eef5ff 100%);
  --dashboard-panel-bg: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  --dashboard-card-bg: rgba(255, 255, 255, 0.9);
  --dashboard-item-bg: #ffffff;
  --dashboard-group-bg: #f8fafc;
  --dashboard-title: #0f172a;
  --dashboard-text: #475569;
  --dashboard-muted: #64748b;
  --dashboard-border: #dbe5f0;
  --dashboard-border-strong: #e2e8f0;
  --dashboard-badge-bg: rgba(13, 110, 253, 0.1);
  --dashboard-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
  --dashboard-panel-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
  --dashboard-hover-border: #bfdbfe;
  --dashboard-hover-shadow: 0 10px 22px rgba(13, 110, 253, 0.08);
}

[data-bs-theme="dark"] .dashboard-shell {
  --dashboard-surface:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.16), transparent 32%),
    linear-gradient(135deg, rgba(7, 12, 24, 0.98) 0%, rgba(15, 23, 42, 0.95) 54%, rgba(18, 27, 45, 0.98) 100%);
  --dashboard-panel-bg: linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(20, 31, 53, 0.92) 100%);
  --dashboard-card-bg: rgba(15, 23, 42, 0.76);
  --dashboard-item-bg: rgba(15, 23, 42, 0.88);
  --dashboard-group-bg: rgba(15, 23, 42, 0.74);
  --dashboard-title: #e5eefb;
  --dashboard-text: #bfd0e6;
  --dashboard-muted: #9fb0c9;
  --dashboard-border: rgba(148, 163, 184, 0.16);
  --dashboard-border-strong: rgba(148, 163, 184, 0.18);
  --dashboard-badge-bg: rgba(59, 130, 246, 0.16);
  --dashboard-shadow: 0 22px 44px rgba(2, 8, 20, 0.34);
  --dashboard-panel-shadow: 0 18px 30px rgba(2, 8, 20, 0.24);
  --dashboard-hover-border: rgba(96, 165, 250, 0.45);
  --dashboard-hover-shadow: 0 12px 24px rgba(2, 8, 20, 0.3);
}

.dashboard-floating-bar {
  position: fixed;
  z-index: 1030;
  pointer-events: auto;
}

.dashboard-floating-bar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 0.95rem;
  border: 1px solid var(--dashboard-border);
  border-radius: 0 0 1.2rem 1.2rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 251, 255, 0.92) 100%);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(16px);
}

[data-bs-theme="dark"] .dashboard-floating-bar-inner {
  background:
    linear-gradient(180deg, rgba(11, 19, 34, 0.94) 0%, rgba(15, 23, 42, 0.92) 100%);
  box-shadow: 0 22px 40px rgba(2, 8, 20, 0.34);
}

.dashboard-floating-primary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.dashboard-floating-secondary {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  min-width: 0;
  flex: 1 1 auto;
}

.dashboard-floating-action {
  min-width: 172px;
  height: 42px;
}

.dashboard-floating-shortcuts {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.35rem;
  min-width: 0;
  overflow-x: auto;
  padding-bottom: 0.15rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.42) transparent;
}

.dashboard-floating-shortcut-group {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0 0.95rem 0 0.2rem;
}

.dashboard-floating-shortcut-group:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 4px;
  right: 0;
  width: 2px;
  height: calc(100% - 8px);
  background: linear-gradient(180deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.9) 48%, rgba(59, 130, 246, 0.05) 100%);
  border-radius: 999px;
}

.dashboard-floating-shortcuts::-webkit-scrollbar {
  height: 6px;
}

.dashboard-floating-shortcuts::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.4);
  border-radius: 999px;
}

.dashboard-floating-shortcut {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  min-width: 2.8rem;
  min-height: 2.8rem;
  padding: 0.35rem;
  color: var(--dashboard-title);
  text-decoration: none;
  background: var(--dashboard-item-bg);
  border: 1px solid var(--dashboard-border-strong);
  border-radius: 0.85rem;
  transition: all 0.2s ease;
}

.dashboard-floating-shortcut:hover {
  color: #0d6efd;
  border-color: var(--dashboard-hover-border);
  box-shadow: var(--dashboard-hover-shadow);
  transform: translateY(-1px);
}

.dashboard-floating-shortcut-icon {
  width: 1.85rem;
  height: 1.85rem;
  font-size: 0.88rem;
}

[data-bs-theme="dark"] .dashboard-floating-shortcut-group:not(:last-child)::after {
  background: linear-gradient(180deg, rgba(96, 165, 250, 0.05) 0%, rgba(96, 165, 250, 0.95) 48%, rgba(96, 165, 250, 0.05) 100%);
}

.dashboard-floating-menu-button {
  display: none;
  align-items: center;
  justify-content: center;
  width: 42px;
  min-width: 42px;
  height: 42px;
  padding: 0;
  font-size: 1.2rem;
}

.dashboard-floating-shortcuts-panel {
  padding: 0 0.95rem 0.95rem;
}

.dashboard-floating-panel-groups {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  padding-top: 0.85rem;
  border-top: 1px solid var(--dashboard-border-strong);
}

.dashboard-floating-panel-group {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.75rem;
  background: var(--dashboard-item-bg);
  border: 1px solid var(--dashboard-border-strong);
  border-radius: 1rem;
}

.dashboard-floating-panel-group-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dashboard-muted);
}

.dashboard-floating-panel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.55rem;
}

.dashboard-floating-shortcut-panel {
  width: 2.9rem;
  min-width: 2.9rem;
}

.dashboard-hero {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 100%;
  padding: 1.5rem;
  border: 1px solid var(--dashboard-border);
  border-radius: 1.5rem;
  background: var(--dashboard-surface);
  box-shadow: var(--dashboard-shadow);
}

.dashboard-hero-top {
  display: flex;
  align-items: flex-start;
  gap: 0;
  flex: 1;
}

.dashboard-hero-main {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-right: 1.75rem;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.35rem 0.75rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0d6efd;
  background: var(--dashboard-badge-bg);
  border-radius: 999px;
}

.hero-title {
  margin: 0;
  font-size: clamp(1.8rem, 2vw, 2.5rem);
  line-height: 1.15;
  color: var(--dashboard-title);
}

.hero-title-highlight {
  color: #198754;
}

.hero-description {
  max-width: 820px;
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  color: var(--dashboard-text);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
}

.hero-action-primary,
.hero-action-secondary {
  min-width: 180px;
  height: 46px;
  border-radius: 0.9rem;
  font-weight: 600;
}

.hero-action-secondary {
  border-color: var(--dashboard-border-strong);
  color: var(--dashboard-title);
  background: var(--dashboard-item-bg);
}

.hero-features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
  margin-top: 0.35rem;
}

.hero-feature {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1rem;
  background: var(--dashboard-card-bg);
  border: 1px solid var(--dashboard-border-strong);
  border-radius: 1rem;
}

.hero-feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.65rem;
  height: 2.65rem;
  font-size: 1.2rem;
  color: #0d6efd;
  background: #e8f1ff;
  border-radius: 0.9rem;
  flex-shrink: 0;
}

.hero-feature-title {
  margin: 0 0 0.2rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--dashboard-title);
}

.hero-feature-text {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--dashboard-muted);
}

.dashboard-hero-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-self: stretch;
  min-width: 260px;
  padding-left: 1.75rem;
  border-left: 1px solid var(--dashboard-border);
}

.hero-side-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem;
  background: var(--dashboard-panel-bg);
  border: 1px solid var(--dashboard-border-strong);
  border-radius: 1.25rem;
  color: var(--dashboard-title);
  box-shadow: var(--dashboard-panel-shadow);
}

.hero-side-card-shortcuts {
  gap: 0.8rem;
}

.hero-side-header {
  display: flex;
  flex-direction: column;
}

.hero-side-kicker {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #2563eb;
}

.hero-side-title {
  margin: 0.45rem 0 0;
  font-size: 1rem;
  line-height: 1.35;
  color: var(--dashboard-title);
}

.hero-shortcut-groups {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.hero-shortcut-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
  padding: 0.5rem 0.75rem;
  background: var(--dashboard-group-bg);
  border: 1px solid var(--dashboard-border-strong);
  border-radius: 0.9rem;
}

.hero-shortcut-group-title {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dashboard-muted);
  line-height: 1.35;
}

.hero-shortcut-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.45rem;
}

.hero-shortcut-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  min-width: 44px;
  min-height: 46px;
  padding: 0.2rem;
  text-align: center;
  text-decoration: none;
  color: var(--dashboard-title);
  background: var(--dashboard-item-bg);
  border: 1px solid var(--dashboard-border-strong);
  border-radius: 0.8rem;
  transition: all 0.2s ease;
}

.hero-shortcut-item:hover {
  color: #0d6efd;
  background: var(--dashboard-item-bg);
  border-color: var(--dashboard-hover-border);
  box-shadow: var(--dashboard-hover-shadow);
  transform: translateY(-2px);
}

.hero-shortcut-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 0.65rem;
  color: #ffffff;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.hero-shortcut-icon-app {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.22);
}

.hero-shortcut-app-image {
  width: 2.5rem;
  height: 2.5rem;
  object-fit: contain;
  display: block;
}

[data-bs-theme="dark"] .hero-shortcut-icon-app {
  background: rgba(255, 255, 255, 0.94);
  border-color: rgba(148, 163, 184, 0.22);
}

.dashboard-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
}

.section-header-inline {
  align-items: center;
}

.section-kicker {
  display: inline-block;
  margin-bottom: 0.3rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #2563eb;
}

.section-title {
  margin: 0;
  font-size: 1.35rem;
  color: var(--dashboard-title);
}

.section-description {
  margin: 0.35rem 0 0;
  color: var(--dashboard-muted);
  line-height: 1.6;
}

.section-action {
  min-width: 168px;
  border-radius: 0.9rem;
  font-weight: 600;
}

.atalho-outlook { background: #0a66c2; }
.atalho-calendar { background: #0078d4; }
.atalho-word { background: #185abd; }
.atalho-excel { background: #1f7244; }
.atalho-planner { background: #0f766e; }
.atalho-powerpoint { background: #c43e1c; }
.atalho-sharepoint { background: #0b7a75; }
.atalho-onedrive { background: #2563eb; }
.atalho-teams { background: #5b5fc7; }
.atalho-contracheque { background: #b45309; }
.atalho-portal { background: #374151; }

[data-bs-theme="dark"] .hero-action-secondary {
  color: var(--dashboard-title);
  background: rgba(15, 23, 42, 0.88);
}

[data-bs-theme="dark"] .hero-action-secondary:hover,
[data-bs-theme="dark"] .hero-action-secondary:focus {
  background: rgba(18, 28, 48, 0.95);
  border-color: var(--dashboard-hover-border);
  color: #ffffff;
}

[data-bs-theme="dark"] .hero-feature-icon {
  background: rgba(59, 130, 246, 0.14);
  color: #60a5fa;
}

.floating-toolbar-enter-active,
.floating-toolbar-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.floating-toolbar-enter-from,
.floating-toolbar-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.floating-shortcuts-panel-enter-active,
.floating-shortcuts-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.floating-shortcuts-panel-enter-from,
.floating-shortcuts-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 1399px) {
  .dashboard-hero-top {
    flex-direction: column;
    gap: 1rem;
  }

  .dashboard-hero-main {
    padding-right: 0;
  }

  .dashboard-hero-side {
    align-self: stretch;
    border-left: none;
    padding-left: 0;
    padding-top: 0;
    min-width: 0;
    margin-top: 20px;
  }

  .dashboard-hero-side::before {
    content: "";
    display: block;
    height: 1px;
    background: var(--dashboard-border);
    margin-bottom: 1.25rem;
  }

  .hero-shortcut-groups {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: start;
    gap: 0.75rem;
  }
}

@media (max-width: 1640px) {
  .dashboard-floating-shortcuts {
    display: none;
  }

  .dashboard-floating-menu-button {
    display: inline-flex;
  }

  .dashboard-floating-bar-inner {
    align-items: center;
  }
}

@media (max-width: 991px) {
  .dashboard-floating-bar-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-floating-primary {
    width: 100%;
  }

  .dashboard-floating-action {
    flex: 1 1 0;
    min-width: 0;
  }

  .dashboard-floating-shortcuts {
    justify-content: flex-start;
  }

  .dashboard-floating-secondary {
    width: 100%;
  }

  .dashboard-floating-menu-button {
    width: 100%;
    min-width: 0;
  }

  .dashboard-hero {
    padding: 1.1rem;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-features {
    grid-template-columns: 1fr;
  }

  .hero-shortcut-group {
    align-items: stretch;
  }

  .hero-shortcut-row {
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .dashboard-shell {
    gap: 1.15rem;
  }

  .dashboard-floating-panel-groups {
    grid-template-columns: 1fr;
  }

  .dashboard-floating-primary {
    flex-direction: column;
  }

  .dashboard-floating-action {
    width: 100%;
  }

  .hero-actions {
    flex-direction: column;
  }

  .hero-action-primary,
  .hero-action-secondary,
  .section-action {
    width: 100%;
  }

  .hero-shortcut-item {
    min-height: 44px;
  }
}
</style>
<script>
/* eslint-disable */

import { defineAsyncComponent } from "vue";
import axios from "axios";
import Layout from "@/layouts/main.vue";
import seiLogo from "@/assets/images/brands/sei.png";

const MICROSOFT_BRAND_ICON_BASE = "https://res.cdn.office.net/files/fabric-cdn-prod_20251117.001/assets/brand-icons/product/svg";

const CardGlpi = defineAsyncComponent(() => import("@/views/mirai/dashboard/cards/glpi.vue"));
const Chamado = defineAsyncComponent(() => import("@/views/mirai/dashboard/chamado.vue"));
const TermosSection = defineAsyncComponent(() => import("@/views/mirai/dashboard/components/TermosSection.vue"));
export default {
  components: {
    Chamado,
    CardGlpi,
    Layout,
    TermosSection,
  },
  data(){
    return {
      user:JSON.parse(localStorage.getItem("userdata") || "{}"),
      termosPendentes: 0,
      chamadosComResposta: 0,
      mostrarBarraFlutuante: false,
      menuAtalhosAberto: false,
      floatingBarLeft: 0,
      floatingBarWidth: 0,
      floatingBarHeight: 0,
      scrollRafId: 0,
      resizeRafId: 0,
      shellObserver: null,
      acoesPainel: [
        {
          titulo: "Abrir solicitações",
          descricao: "Registre suporte para sistemas, acessos, equipamentos e demais demandas de TI.",
          icone: "ri-customer-service-2-line",
        },
        {
          titulo: "Acompanhar andamento",
          descricao: "Consulte status, histórico e atualizações dos chamados já registrados.",
          icone: "ri-line-chart-line",
        },
        {
          titulo: "Navegar com agilidade",
          descricao: "Entre rapidamente nas ferramentas institucionais mais utilizadas pela equipe.",
          icone: "ri-links-line",
        },
      ],
      atalhosRapidos: [

        {
          titulo: "Comunicação e colaboração",
          descricao: "Acesso rápido a e-mail, equipes, arquivos compartilhados e colaboração.",
          itens: [
            { titulo: "Outlook", link: "https://outlook.office.com/", icone: "ri-mail-line", cor: "atalho-outlook", imagem: `${MICROSOFT_BRAND_ICON_BASE}/outlook_48x1.svg` },
            { titulo: "Calendário", link: "https://outlook.cloud.microsoft/calendar/view/workweek", icone: "ri-calendar-line", cor: "atalho-calendar", imagem: `${MICROSOFT_BRAND_ICON_BASE}/outlook_48x1.svg` },
            { titulo: "Teams", link: "https://teams.microsoft.com/", icone: "ri-team-line", cor: "atalho-teams", imagem: `${MICROSOFT_BRAND_ICON_BASE}/teams_48x1.svg` },
            { titulo: "SharePoint", link: "https://www.office.com/launch/sharepoint", icone: "ri-share-forward-line", cor: "atalho-sharepoint", imagem: `${MICROSOFT_BRAND_ICON_BASE}/sharepoint_48x1.svg` },
            { titulo: "OneDrive", link: "https://prefeituracontagem-my.sharepoint.com/", icone: "ri-cloud-line", cor: "atalho-onedrive", imagem: `${MICROSOFT_BRAND_ICON_BASE}/onedrive_48x1.svg` },
          ],
        },
        {
          titulo: "Produtividade",
          descricao: "Ferramentas de edição e apresentação para atividades do dia a dia.",
          itens: [
            { titulo: "Word", link: "https://www.office.com/launch/word", icone: "ri-file-word-2-line", cor: "atalho-word", imagem: `${MICROSOFT_BRAND_ICON_BASE}/word_48x1.svg` },
            { titulo: "Excel", link: "https://www.office.com/launch/excel", icone: "ri-file-excel-2-line", cor: "atalho-excel", imagem: `${MICROSOFT_BRAND_ICON_BASE}/excel_48x1.svg` },
            { titulo: "Planner", link: "https://planner.cloud.microsoft/", icone: "ri-kanban-view-2", cor: "atalho-planner", imagem: "https://planner.cloud.microsoft/images/planner_icon.png" },
            { titulo: "PowerPoint", link: "https://www.office.com/launch/powerpoint", icone: "ri-file-ppt-2-line", cor: "atalho-powerpoint", imagem: `${MICROSOFT_BRAND_ICON_BASE}/powerpoint_48x1.svg` },
          ],
        },
        {
          titulo: "Serviços institucionais",
          descricao: "Consultas e acessos internos para rotinas administrativas do servidor.",
          itens: [
            { titulo: "SEI", link: "https://sei.contagem.mg.gov.br", icone: "ri-file-list-3-line", cor: "atalho-portal", imagem: seiLogo },
            //{ titulo: "Portal do Servidor", link: "https://portal.contagem.mg.gov.br/portal/paginas-dinamicas-categoria/15/portal-do-servidor", icone: "ri-government-line", cor: "atalho-portal" },
            //{ titulo: "Contracheque", link: "https://maxnet.contagem.mg.gov.br/ADI_Intranet_Root/ADI_Programacao/ADI_MaxNetADI/startupMaxNet/login.jsp?sec=65a26ddb45556c0e7014d2e4013cb68d", icone: "ri-money-dollar-circle-line", cor: "atalho-contracheque" },
          ],
        },

      ],
    }
  },
  computed: {
    atalhosRapidosOrdenados() {
      const colator = new Intl.Collator("pt-BR", { sensitivity: "base" });
      return (this.atalhosRapidos || []).map((categoria) => ({
        ...categoria,
        itens: Array.isArray(categoria?.itens)
          ? [...categoria.itens].sort((a, b) => colator.compare(a?.titulo || "", b?.titulo || ""))
          : [],
      }));
    },
    atalhosBarraFlutuante() {
      return (this.atalhosRapidosOrdenados || []).reduce((acc, categoria) => {
        const itens = Array.isArray(categoria?.itens) ? categoria.itens : [];
        return acc.concat(itens);
      }, []);
    },
    floatingBarStyle() {
      return {
        top: "0px",
        left: "0px",
        width: "100%",
      };
    },
  },
  created() {
    // `termosPendentes` vem por evento da própria seção de termos — uma fonte
    // de verdade só, para o aviso do topo não divergir da lista.
    this.carregarChamadosComResposta();
  },
  async mounted() {
    this.$nextTick(() => {
      this.atualizarGeometriaBarraFlutuante();
      this.atualizarVisibilidadeBarraFlutuante();
      this.atualizarAlturaBarraFlutuante();
      window.addEventListener("scroll", this.onScrollBarraFlutuante, { passive: true });
      window.addEventListener("resize", this.onResizeBarraFlutuante, { passive: true });
      this.iniciarObserversBarraFlutuante();
    });
  },
  methods:{
    getTriggerElementBarraFlutuante() {
      if (window.innerWidth <= 1399) {
        return this.$refs.dashboardShortcutPanel || this.$refs.dashboardHeroActions || this.$refs.dashboardHeroGrid;
      }
      return this.$refs.dashboardHeroActions || this.$refs.dashboardHeroGrid;
    },
    getAtalhoPopoverId(prefixo, categoriaTitulo, atalhoTitulo) {
      const normalizar = (valor) => String(valor || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      return `atalho-${prefixo}-${normalizar(categoriaTitulo)}-${normalizar(atalhoTitulo)}`;
    },
    // Termo de responsabilidade pendente é obrigação do usuário, não aviso
    // opcional: aparece em destaque logo no topo do painel.
    // Termos são uma seção desta mesma página: rolar preserva o contexto,
    // igual ao "Ver meus chamados".
    irParaTermos() {
      const area = document.getElementById("area-termos");
      if (!area) return;
      const offsetExtra = (this.mostrarBarraFlutuante ? this.floatingBarHeight : 0) + 16;
      const top = area.getBoundingClientRect().top + window.scrollY - offsetExtra;
      window.scrollTo({ top, behavior: "smooth" });
    },
    // Quantos chamados receberam resposta da equipe e ainda não foram lidos.
    async carregarChamadosComResposta() {
      try {
        const resposta = await axios.get("/tarefa/interacoes");
        this.chamadosComResposta = Number(resposta?.data?.total_nao_lidos || 0);
      } catch (_e) {
        this.chamadosComResposta = 0;
      }
    },
    abrirNovoChamado() {
      try {
        this.$refs.chamados.openNovoChamado();
      } catch (e) {
        console.error(e);
      }
    },
    irParaChamados() {
      const areaChamados = document.getElementById("area-chamados");
      if (areaChamados) {
        const offsetExtra = (this.mostrarBarraFlutuante ? this.floatingBarHeight : 0) + 16;
        const top = areaChamados.getBoundingClientRect().top + window.scrollY - offsetExtra;
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
    toggleAtalhosBarraFlutuante() {
      this.menuAtalhosAberto = !this.menuAtalhosAberto;
      this.atualizarAlturaBarraFlutuante();
    },
    fecharAtalhosBarraFlutuante() {
      if (!this.menuAtalhosAberto) return;
      this.menuAtalhosAberto = false;
      this.atualizarAlturaBarraFlutuante();
    },
    onScrollBarraFlutuante() {
      if (this.scrollRafId) return;
      this.scrollRafId = window.requestAnimationFrame(() => {
        this.scrollRafId = 0;
        this.atualizarVisibilidadeBarraFlutuante();
      });
    },
    onResizeBarraFlutuante() {
      if (this.resizeRafId) {
        window.cancelAnimationFrame(this.resizeRafId);
      }
      this.resizeRafId = window.requestAnimationFrame(() => {
        this.resizeRafId = 0;
        if (window.innerWidth > 1630) {
          this.menuAtalhosAberto = false;
        }
        this.atualizarGeometriaBarraFlutuante();
        this.atualizarAlturaBarraFlutuante();
        this.atualizarVisibilidadeBarraFlutuante();
      });
    },
    iniciarObserversBarraFlutuante() {
      const dashboardShell = this.$refs.dashboardShell;
      if (dashboardShell && typeof ResizeObserver !== "undefined") {
        this.shellObserver = new ResizeObserver(() => {
          this.atualizarGeometriaBarraFlutuante();
          this.atualizarAlturaBarraFlutuante();
        });
        this.shellObserver.observe(dashboardShell);
      }
    },
    atualizarGeometriaBarraFlutuante() {
      const dashboardShell = this.$refs.dashboardShell;
      if (!dashboardShell) return;
      const rect = dashboardShell.getBoundingClientRect();
      this.floatingBarLeft = Math.max(Math.round(rect.left), 0);
      this.floatingBarWidth = Math.round(rect.width);
    },
    atualizarAlturaBarraFlutuante() {
      this.$nextTick(() => {
        this.floatingBarHeight = this.$refs.dashboardFloatingBar?.offsetHeight || 0;
      });
    },
    atualizarVisibilidadeBarraFlutuante() {
      const triggerElement = this.getTriggerElementBarraFlutuante();
      if (!triggerElement) return;

      const limite = 48;
      const triggerRect = triggerElement.getBoundingClientRect();
      const deveMostrar = window.innerWidth <= 1399
        ? triggerRect.top <= limite
        : triggerRect.bottom <= limite;

      if (deveMostrar !== this.mostrarBarraFlutuante) {
        this.mostrarBarraFlutuante = deveMostrar;
        if (!deveMostrar) {
          this.menuAtalhosAberto = false;
        }
        this.atualizarGeometriaBarraFlutuante();
        this.atualizarAlturaBarraFlutuante();
      }
    },
    createEvent(){
      try{
        this.$refs["card-glpi"].getStart();
      }catch (e) {
        console.error(e);
      }
    }
  },
  beforeUnmount() {
    clearInterval(this.interval);
    window.removeEventListener("scroll", this.onScrollBarraFlutuante);
    window.removeEventListener("resize", this.onResizeBarraFlutuante);
    if (this.scrollRafId) {
      window.cancelAnimationFrame(this.scrollRafId);
    }
    if (this.resizeRafId) {
      window.cancelAnimationFrame(this.resizeRafId);
    }
    this.shellObserver?.disconnect?.();
  },
};
</script>
